import { useState, useEffect, useRef } from 'react';
import './ResearchResults.css';
import { primeYouTubeCache } from '../utils/youtubeCache';
import { getAuthHeaders } from '../utils/odotId';
import ReportView from './ReportView';
import { ErrorBoundary } from './ErrorBoundary';
import { FloatingStatus } from './FloatingStatus';
import type { Report } from '@tastymusic/research-agent';

interface QueryResult {
  genres: string[];
  mood: string[];
  artistType: string | null;
  likeArtists: string[];
  unlikeArtists: string[];
  modifiers: string[];
  attitude: string[];
  era: string | null;
  location: string | null;
  freeform: string;
}

interface GenreContext {
  description: string;
  keyArtists: {
    name: string;
    track: string;
    links?: {
      bandcamp?: string;
      spotify?: string;
      youtube?: string;
    };
  }[];
  youtubeQueries: string[];
}

interface BandcampEmbedData {
  embedHtml: string;
  albumId?: string;
  title?: string;
}

interface ArtistResult {
  name: string;
  genres: string[];
  era: string | null;
  location: string | null;
  matchReason: string;
  confidence: number;
  tracks: string[];
  relatedArtists: string[];
  links?: {
    bandcamp?: string;
    spotify?: string;
    youtube?: string;
  };
  // Pre-resolved embed data
  youtubeVideoId?: string;
  bandcampEmbed?: BandcampEmbedData;
}

interface ResearchResultsProps {
  query: QueryResult;
  onReset: () => void;
  theme?: 'light' | 'dark';
}

type ResearchPhase = 'starting' | 'context' | 'searching' | 'deep_research' | 'report' | 'complete' | 'cached';

// Tab IDs matching ReportView
type TabId = 'discover' | 'genre' | 'scenes' | 'artists' | 'mutations' | 'infrastructure' | 'coda' | 'reference';

// Map section types to tab IDs for progressive building indicator
const sectionToTab: Record<string, TabId | undefined> = {
  'introduction': 'genre',
  'founders': 'genre',
  'scene': 'scenes',
  'mutation': 'mutations',
  'moreArtists': 'artists',
  'infrastructure': 'infrastructure',
  'coda': 'coda',
  'quickReference': 'reference',
};

// Extract headline from status message like "Researching female trap..." -> "Female Trap"
function extractHeadline(message: string): string {
  const match = message.match(/^Researching\s+(.+?)\.{0,3}$/i);
  if (match) {
    return match[1]
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }
  return '';
}

export function ResearchResults({ query, onReset, theme = 'light' }: ResearchResultsProps) {
  const [phase, setPhase] = useState<ResearchPhase>('starting');
  const [statusMessage, setStatusMessage] = useState('');
  const [headline, setHeadline] = useState('');
  const [context, setContext] = useState<GenreContext | null>(null);
  const [artists, setArtists] = useState<ArtistResult[]>([]);
  const [report, setReport] = useState<Report | null>(null);
  const [youtubeCache, setYoutubeCache] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [isCached, setIsCached] = useState(false);
  const [buildingTab, setBuildingTab] = useState<TabId | undefined>(undefined);
  const [startTime] = useState(() => Date.now()); // Track when research started
  const eventSourceRef = useRef<EventSource | null>(null);

  useEffect(() => {
    startResearch();

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, []);

  const startResearch = async () => {
    setPhase('starting');
    setStatusMessage('Initializing research...');

    try {
      // Use fetch with SSE via POST
      const response = await fetch('/api/v1/research', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        credentials: 'include',
        body: JSON.stringify(query),
      });

      if (!response.ok) {
        throw new Error('Research request failed');
      }

      // Read SSE stream
      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No response body');
      }

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              handleEvent(data);
            } catch (e) {
              console.error('Failed to parse SSE data:', e);
            }
          }
        }
      }
    } catch (err) {
      console.error('Research error:', err);
      setError(err instanceof Error ? err.message : 'Research failed');
    }
  };

  const handleEvent = (event: { type: string; [key: string]: unknown }) => {
    switch (event.type) {
      case 'status':
        setPhase(event.phase as ResearchPhase);
        setStatusMessage(event.message as string);
        // Extract headline from "Researching X..." message
        if (event.phase === 'context') {
          const extracted = extractHeadline(event.message as string);
          if (extracted) setHeadline(extracted);
        }
        if (event.phase === 'cached') {
          setIsCached(true);
        }
        break;
      case 'report-id':
        // Report ID received early - update URL so user can recover if they bail
        if (event.reportId) {
          window.history.replaceState({}, '', `/report/${event.reportId}`);
        }
        break;
      case 'redirect':
        // Another request is already generating this report - redirect to it
        if (event.reportId) {
          console.log(`[research] Redirecting to in-progress report: ${event.reportId}`);
          window.location.href = `/report/${event.reportId}`;
        }
        break;
      case 'context':
        setContext(event.context as GenreContext);
        break;
      case 'artist':
        setArtists((prev) => {
          const newArtist = event.artist as ArtistResult;
          // Include pre-resolved embed data from event
          if (event.youtubeVideoId) {
            newArtist.youtubeVideoId = event.youtubeVideoId as string;
          }
          if (event.bandcampEmbed) {
            newArtist.bandcampEmbed = event.bandcampEmbed as BandcampEmbedData;
          }
          // Deduplicate by artist name (case-insensitive)
          if (prev.some(a => a.name.toLowerCase() === newArtist.name.toLowerCase())) {
            return prev;
          }
          return [...prev, newArtist];
        });
        break;
      case 'section':
        // Progressive section received - update report incrementally
        {
          const sectionType = event.sectionType as string;
          const data = event.data as Record<string, unknown>;

          // Update building tab indicator
          const tabForSection = sectionToTab[sectionType];
          if (tabForSection) {
            setBuildingTab(tabForSection);
          }

          setReport((prev) => {

          // Header is special - it contains top-level props (title, subtitle, tagline)
          if (sectionType === 'header') {
            return { ...(prev || {}), ...data } as Report;
          }

          if (!prev) {
            // Initialize report with first section
            const key = getSectionKey(sectionType);
            if (sectionType === 'scene') {
              return { scenes: [data] } as unknown as Report;
            } else if (sectionType === 'mutation') {
              return { mutations: [data] } as unknown as Report;
            }
            return { [key]: data } as unknown as Report;
          }

          // Merge new section into existing report
          const key = getSectionKey(sectionType);
          if (sectionType === 'scene') {
            // Append to scenes array
            const scenes = (prev.scenes || []) as unknown[];
            return { ...prev, scenes: [...scenes, data] } as Report;
          } else if (sectionType === 'mutation') {
            // Append to mutations array
            const mutations = (prev.mutations || []) as unknown[];
            return { ...prev, mutations: [...mutations, data] } as Report;
          } else {
            return { ...prev, [key]: data } as Report;
          }
        });
        }
        break;
      case 'report':
        // Full editorial report received (fallback/final)
        setReport(event.report as Report);
        const cache = event.youtubeCache as Record<string, string>;
        setYoutubeCache(cache);
        // Prime YouTube cache with pre-resolved video IDs
        if (cache) {
          primeYouTubeCache(cache);
        }
        break;
      case 'complete':
        setPhase('complete');
        setStatusMessage(`Found ${event.totalArtists || artists.length} artists`);
        setBuildingTab(undefined); // Clear building indicator
        if (event.cached) {
          setIsCached(true);
        }
        break;
      case 'error':
        setError(event.message as string);
        break;
    }
  };

  // Map section type to report key
  const getSectionKey = (sectionType: string): string => {
    const mapping: Record<string, string> = {
      'header': 'title', // special case - merged directly
      'introduction': 'introduction',
      'founders': 'founders',
      'scene': 'scenes', // array
      'mutation': 'mutations', // array
      'moreArtists': 'moreArtists',
      'infrastructure': 'infrastructure',
      'coda': 'coda',
      'quickReference': 'quickReference',
    };
    return mapping[sectionType] || sectionType;
  };

  const getPlatformLinks = (artistName: string) => {
    const encoded = encodeURIComponent(artistName);
    return {
      // Bandcamp first - support artists directly
      bandcamp: `https://bandcamp.com/search?q=${encoded}`,
      spotify: `https://open.spotify.com/search/${encoded}`,
      apple: `https://music.apple.com/search?term=${encoded}`,
      tidal: `https://tidal.com/search?q=${encoded}`,
      amazon: `https://music.amazon.com/search/${encoded}`,
    };
  };

  const handleRetry = () => {
    setError(null);
    setPhase('starting');
    setContext(null);
    setArtists([]);
    setReport(null);
    setYoutubeCache({});
    setIsCached(false);
    setBuildingTab(undefined);
    startResearch();
  };

  if (error) {
    return (
      <div className="research-results error">
        <div className="error-content">
          <p className="error-message">Research failed</p>
          <p className="error-detail">{error}</p>
          <div className="error-actions">
            <button className="retry-button" onClick={handleRetry}>Try again</button>
            <button className="reset-button" onClick={onReset}>New search</button>
          </div>
        </div>
      </div>
    );
  }

  // Create a minimal report shell for early rendering if needed
  const reportForView = report || (context || artists.length > 0 ? { title: headline } as Report : null);

  return (
    <div className="research-results">
      {/* Report with Discover tab - renders when we have context, artists, or report */}
      {reportForView && (
        <div className="report-container">
          <ErrorBoundary>
            <ReportView
              report={reportForView}
              youtubeCache={youtubeCache}
              theme={theme}
              genreContext={context || undefined}
              discoveredArtists={artists}
              extraArtists={artists.length > 6 ? artists.slice(6) : ((report && 'discoveredArtists' in report ? report.discoveredArtists : []) as ArtistResult[] || []).slice(6)}
              getPlatformLinks={getPlatformLinks}
              buildingTab={isCached ? undefined : buildingTab}
            />
          </ErrorBoundary>
        </div>
      )}

      {/* Actions */}
      <div className="research-actions">
        <button className="reset-button" onClick={onReset}>
          New search
        </button>
      </div>

      {/* Floating status indicator */}
      <FloatingStatus
        phase={phase}
        message={statusMessage}
        isActive={phase !== 'complete' && !isCached && !error}
        query={headline || query.freeform}
        theme={theme}
        startTime={startTime}
      />
    </div>
  );
}
